import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom';

import { Layout } from './components/Layout';
import { Home as HomeIcon, Loader2, Compass } from 'lucide-react';
import { SiteProvider, useSiteContent } from './context/SiteContext';
import { ToastContainer } from './components/ToastContainer';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SEO } from './components/SEO';

// Import Home directly to avoid Suspense issues during debug
import { Home } from './pages/Home';

// Lazy Load Pages for Performance
const Services = lazy(() => import('./pages/Services').then(module => ({ default: module.Services })));
const Projects = lazy(() => import('./pages/Projects').then(module => ({ default: module.Projects })));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail').then(module => ({ default: module.ProjectDetail })));
const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));
const Contact = lazy(() => import('./pages/Contact').then(module => ({ default: module.Contact })));
const Admin = lazy(() => import('./pages/Admin').then(module => ({ default: module.Admin })));
const Login = lazy(() => import('./pages/Login').then(module => ({ default: module.Login })));

// Component to handle global theme side-effects
const ThemeHandler = () => {
  const { theme } = useSiteContent();

  useEffect(() => {
    console.log('ThemeHandler: Changing theme to', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  return null;
};

// UX Fix: Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Loading Component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background-light dark:bg-background-dark text-primary">
    <Loader2 size={48} className="animate-spin" />
  </div>
);

// Page Wrapper for Animations
const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return (
    <div key={location.pathname} className="page-transition w-full">
      {children}
    </div>
  );
};

// 404 Page Component (Enhanced)
const NotFound = () => (
  <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-4 relative overflow-hidden">
    {/* Animated Background Blobs */}
    <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30">
      <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
      <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
    </div>

    <div className="relative z-10 bg-white/50 dark:bg-surface-dark/50 backdrop-blur-xl border border-white/20 dark:border-white/5 p-10 rounded-3xl shadow-2xl max-w-lg w-full">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 dark:bg-red-500/10 text-red-500 mb-6 animate-bounce">
        <Compass size={40} />
      </div>
      <h1 className="text-5xl font-black text-slate-900 dark:text-white mb-2">404</h1>
      <h2 className="text-xl font-bold text-slate-700 dark:text-gray-300 mb-4">Kaybolmuş Olabilirsiniz</h2>
      <p className="text-gray-500 dark:text-gray-400 mb-8 leading-relaxed">
        Aradığınız sayfa silinmiş, taşınmış veya hiç var olmamış olabilir. Ancak endişelenmeyin, sizi ana sayfaya götürebiliriz.
      </p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white rounded-xl font-bold hover:bg-primary-hover transition-all shadow-lg hover:shadow-primary/25 hover:-translate-y-1 active:scale-95"
      >
        <HomeIcon size={20} /> Ana Sayfaya Dön
      </Link>
    </div>
  </div>
);

const AppRoutes = () => {
  const location = useLocation();
  console.log('AppRoutes Rendering, path:', location.pathname);

  // Admin & Login routes do not use the standard layout
  if (location.pathname.startsWith('/admin') || location.pathname === '/login') {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <PageWrapper>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageWrapper>
      </Suspense>
    </Layout>
  );
};

export default function App() {
  console.log('App Rendering...');
  return (
    <ErrorBoundary>
      <SiteProvider>
        <BrowserRouter>
          <SEO />
          <ScrollToTop />
          <ToastContainer />
          <AppRoutes />
        </BrowserRouter>
      </SiteProvider>
    </ErrorBoundary >
  );
}